import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import {
  selectedBranchAtom,
  searchValuesAtom,
  openSnackbarAdviseAtom,
  activityTypesAtom,
} from '../../Atoms';
import useAPI from '../../api';
import { download } from '../../api/utils';
import SectionPage from '../../Components/Paper/SectionPage';
import PracticeDialog from './Components/Dialog';
import mapFilters from '../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import PracticeListItem from './Components/PracticeListItem';
import ErrorDialog from '../../Components/Error';
import ConfirmPopup from '../../Components/Popup/ConfirmPopup';
import PracticeFilters from './Components/PracticeFilters';
import PracticesFab from './Components/PracticesFab';
import { checkFeature } from '../../Router/routes';
import { PRACTICE_SORTING } from './Utils/constants';
import keysMapper from './Utils/keysMapper';
import { formatPayloadDate } from '../../Helpers/dates';
import getErrorMessage from '../../Helpers/Errors/getErrorMessage';

const getPracticeListItem = (data, onClick, onDelete) => (
  <PracticeListItem
    key={data.id}
    data={data}
    onClick={onClick}
    onDelete={onDelete}
  />
);

const normalizeDateFieldsForPayload = (payload) =>
  Object.fromEntries(
    Object.entries(payload).map(([key, value]) => {
      if (value instanceof Date && !isNaN(value.getTime())) {
        return [key, formatPayloadDate(value)?.toISOString()];
      }
      return [key, value];
    })
  );

const PracticesPage = function PracticesPage() {
  const API = useAPI();
  const intl = useIntl();

  const activityTypes = useAtomValue(activityTypesAtom);
  const [practiceDialog, setPracticeDialog] = useState(null);
  const [confirmClosing, setConfirmClosing] = useState(null);
  const [error, setError] = useState(null);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const openNewPracticeDialog = (customerData) => {
    setPracticeDialog({ ...keysMapper(customerData), dialogType: 'NEW' });
  };

  const openEditPracticeDialog = (practiceData) => {
    setPracticeDialog({ ...practiceData, dialogType: 'EDIT' });
  };

  const downloadAllPracticesFile = useCallback(
    () =>
      download(
        `Practices-${selectedBranch.branchDescription}`,
        API.export2ExcelPracticesHistory(selectedBranch.id, {
          pageNumber: 0,
          filterExpression: mapFilters(
            ['fullNameSearch', 'fiscalCode', 'practiceNumber'],
            [{ keyName: 'practiceState', operator: 'eq' }],
            [],
            searchValues
          ),
          sortExpression: [
            {
              sortValue: searchValues?.sortValue.value,
              sort: searchValues?.sortValue.direction,
            },
            {
              sortValue: 'customerName',
              sort: 'asc',
            },
            {
              sortValue: 'customerSurname',
              sort: 'asc',
            },
          ],
          silent: false,
        })
      ),
    [selectedBranch, searchValues, error]
  );

  const getPractices = useCallback(
    (pageNumber) => {
      return selectedBranch && !error
        ? API.getPracticesHistory(
            selectedBranch.id,
            {
              pageNumber,
              silent: pageNumber > 1,
              filterExpression: mapFilters(
                ['fullNameSearch', 'fiscalCode', 'practiceNumber'],
                [{ keyName: 'practiceState', operator: 'eq' }],
                [
                  { keyName: 'dateLoadingPractice', includeExtremes: true },
                  { keyName: 'liquidationDate', includeExtremes: true },
                ],
                searchValues
              ),
              sortExpression: [
                {
                  sortValue: searchValues?.sortValue?.value,
                  sort: searchValues?.sortValue?.direction,
                },
                {
                  sortValue: 'customerName',
                  sort: 'asc',
                },
                {
                  sortValue: 'customerSurname',
                  sort: 'asc',
                },
              ],
            },
            () => {
              saveFiltersInLocal(searchValues);
            },
            (err) => {
              setError(err);
            }
          )
        : Promise.resolve([]);
    },
    [selectedBranch, searchValues, error]
  );

  const {
    ObserverWrapper,
    recordCount,
    updateItem,
    removeClickedItem,
    reload,
  } = useInfiniteQuery(getPractices);

  const handleSubmit = (data) => {
    if (data) {
      const submitData = { ...data }; //creates a copy to not mutate the original data

      //removes spaces before and after string
      Object.keys(submitData).forEach((keyName) => {
        submitData[keyName] =
          typeof submitData[keyName] === 'string'
            ? submitData[keyName]?.trim()
            : submitData[keyName];
      });

      //converts empty strings to null and parsable strings to numbers
      Object.keys(submitData).forEach((keyName) => {
        const excludedFields = [
          'fiscalCode',
          'zipCode',
          'zip',
          'postalCode',
          'cap',
          'practiceNumber',
        ];
        const isExcluded = excludedFields.some((field) =>
          keyName.toLowerCase().includes(field.toLowerCase())
        );

        if (submitData[keyName] === '') {
          submitData[keyName] = null;
        } else if (typeof submitData[keyName] === 'string' && !isExcluded) {
          const value = submitData[keyName];
          // Only parse if it's a pure number (not ranges like "31-45" or other patterns)
          const parsed = parseFloat(value);
          if (!isNaN(parsed) && /^-?\d+\.?\d*$/.test(value)) {
            // Check if it's an integer
            submitData[keyName] = Number.isInteger(parsed)
              ? parseInt(value, 10)
              : parsed;
          }
        }
      });

      const payloadData =
        submitData.dialogType === 'NEW'
          ? normalizeDateFieldsForPayload(submitData)
          : submitData;

      const { outcome, entityId, campaignId, parentId, ...rest } =
        payloadData || {};
      if (payloadData.dialogType === 'NEW')
        API.activity(
          {
            outcome,
            branchId: selectedBranch.id,
            entityId: entityId,
            notes: rest?.notes,
            campaignId: campaignId,
            parentId: parentId,
            activityTypeId: activityTypes.find(
              (type) => type.name === 'DOSSIER'
            ).id,
          },
          payloadData,
          {},
          () => {
            reload();
            setPracticeDialog(null);
            setOpenSnackbarAdvise({
              message: intl.messages.PRACTICE_INSERTED_SUCCESSFULLY,
              severity: 'success',
            });
          },
          (err) => {
            const serverMessage = getErrorMessage(err, intl);
            setOpenSnackbarAdvise({
              message: `${intl.messages.ERROR}: ${
                err?.status ? `${err?.status} - ` : ''
              }${serverMessage?.includes('PracticeNumber duplicated') ? intl.messages.PRACTICE_NUMBER_DUPLICATED : serverMessage || intl.messages.IMPORT_PRACTICES_ERROR}`,
              severity: 'error',
            });
          }
        );
      else
        API.editPractice(
          {
            ...submitData,
            productCode: submitData?.productCode?.toString(),
          },
          (updatedPracticeData) => {
            updateItem(updatedPracticeData);
            setPracticeDialog(null);
            setOpenSnackbarAdvise({
              message: intl.messages.PRACTICE_EDITED_SUCCESSFULLY,
              severity: 'success',
            });
          },
          (err) => {
            setOpenSnackbarAdvise({
              message: `${intl.messages.ERROR}: ${
                err?.status ? `${err?.status} - ` : ''
              } ${intl.messages.EDITING_PRACTICE_ERROR}`,
              severity: 'error',
            });
          }
        );
    }
  };

  const handleDeletePractice = (practiceId) => {
    API.deletePractice(
      practiceId,
      () => {
        removeClickedItem(practiceId, recordCount - 1);
        setOpenSnackbarAdvise({
          message: intl.messages.PRACTICE_DELETED,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.PRACTICE_DELETE_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleImportCsv = (file) => {
    const formData = new FormData();
    formData.append('file', file);

    API.importPractices(
      selectedBranch.id,
      formData,
      () => {
        reload();
        setOpenSnackbarAdvise({
          message: intl.messages.PRACTICES_IMPORTED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        const serverMessage = getErrorMessage(err, intl);
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          }${serverMessage || intl.messages.IMPORT_PRACTICES_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const handleChangeFilters = ({
    selectedStatus,
    dateLoadingPractice,
    liquidationDate,
  }) => {
    setSearchValues((prev) => ({
      ...prev,
      ...{
        practiceState: selectedStatus,
        dateLoadingPractice,
        liquidationDate,
      },
    }));
  };

  return (
    <>
      <SectionPage
        sectionName="PRACTICES"
        fab={intl.messages.NEW_PRACTICE}
        onClickFab={openNewPracticeDialog}
        fabVariant="extended"
        customFab={
          checkFeature('PRACTICES_IMPORT_FEATURES') ? (
            <PracticesFab
              branchName={selectedBranch?.branchDescription}
              onNewPractice={openNewPracticeDialog}
              onUploadCsv={handleImportCsv}
            />
          ) : null
        }
        ListObserverWrapper={ObserverWrapper}
        customEntryFunction={(item) =>
          getPracticeListItem(
            item,
            openEditPracticeDialog,
            handleDeletePractice
          )
        }
        customFilters={
          <PracticeFilters
            defaultValue={{
              practiceState:
                localStorage.getItem('practiceState') === 'undefined'
                  ? null
                  : JSON.parse(localStorage.getItem('practiceState')),
              liquidationDate: searchValues?.liquidationDate,
              dateLoadingPractice: searchValues?.dateLoadingPractice,
            }}
            onChangeFilters={handleChangeFilters}
          />
        }
        sortingOptions={PRACTICE_SORTING}
        filters={[{ keyName: 'practiceState' }]}
        ExportToExcel={downloadAllPracticesFile}
      />
      <PracticeDialog
        open={Boolean(practiceDialog)}
        data={practiceDialog}
        onClose={(e, isFormDirty) => {
          if (isFormDirty) setConfirmClosing(e.currentTarget);
          else {
            setPracticeDialog(null);
          }
        }}
        onSubmit={handleSubmit}
        edit={practiceDialog?.dialogType === 'EDIT'}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={() => setConfirmClosing(null)}
        handleConfirm={() => setPracticeDialog(null)}
      />
      <ErrorDialog error={error} handleRetry={() => setError(null)} />
    </>
  );
};

PracticesPage.propTypes = {
  heightQuery: PropTypes.bool,
};

export default PracticesPage;
